import Portal from '@ui/portal/portal';
import styles from './preview-stylepack.module.css';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectStylepacks } from '@/store/slices/stylepacks/selectors';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import PillTabs from '@ui/tabs/pill-tabs';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';
import { VideoType, type ContentTemplate, videoTypeMap, stylepackContentTypeMap } from '@/types/stylepacks.types';
import classNames from 'classnames';
import VideoPlayer from './video-player';
import { getWorkingProject, selectWorkingSession } from '@/store/slices/project/selectors';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import toastTrigger from '@ui/toast/toast-trigger';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { addToRecentStylepacks } from '../utils/stylepack-utils';
import { useGreenroomStylepackShots } from '../utils/use-greenroom-stylepack-shots';
import type { GraphicItem } from '../components/video-list-graphic.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useStylepackAudio } from '../utils/use-stylepack-audio';

export default function PreviewStylepack() {
  const { stylepackId } = useParams<{ stylepackId: string }>();
  const stylepacks = useAppSelector(selectStylepacks);
  const session = useAppSelector(selectWorkingSession);
  const sessionStylepackId = session?.streaming_options.stylepackId;

  const [type, setType] = useState(VideoType.all);
  const [availableTypes, setAvailableTypes] = useState<VideoType[]>(Object.values(VideoType));
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const workingProject = useAppSelector(getWorkingProject);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const previewingShots = stylepackId === 'greenroom';

  const targetSession = workingProject?.sessions[0];

  const selectedStylepack = useMemo(() => {
    if (stylepackId === 'greenroom' && sessionStylepackId) {
      return stylepacks?.find((pack) => pack.stylePack === sessionStylepackId);
    }

    return stylepacks?.find((pack) => pack.stylePack === stylepackId);
  }, [sessionStylepackId, stylepackId, stylepacks]);

  useEffect(() => {
    if (selectedStylepack?.videos) {
      const availableTypes = selectedStylepack.videos
        .map((video: ContentTemplate) => (video.contentType ? stylepackContentTypeMap[video.contentType] : undefined))
        .filter((type) => !!type);

      setAvailableTypes([VideoType.all, ...Array.from(new Set(availableTypes))]);
    } else {
      setAvailableTypes(Object.values(VideoType));
    }
  }, [selectedStylepack?.videos]);

  const contentTemplates = useMemo(() => {
    return (
      selectedStylepack?.videos.filter((video) => type === VideoType.all || videoTypeMap[type] === video.contentType) ||
      []
    );
  }, [selectedStylepack?.videos, type]);

  const greenroomVideos = useGreenroomStylepackShots(selectedStylepack, type);

  const videos = previewingShots ? greenroomVideos : contentTemplates;

  const chooseStylepack = useCallback(async () => {
    if (!targetSession || !stylepackId) return;

    try {
      setSaving(true);
      await dispatch(
        saveWorkingProjectSessionByField({
          uuid: targetSession.uuid,
          streaming_options: {
            ...targetSession.streaming_options,
            stylepackId,
          },
        })
      );

      addToRecentStylepacks(stylepackId);
      navigate('../../', { relative: 'path' });
    } catch (e) {
      console.error(e);
      toastTrigger('Error selecting stylepack', { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [dispatch, navigate, stylepackId, targetSession]);

  const nextVideo = useCallback(() => {
    if (!videos.length) return;

    setSelectedVideoIndex((prev) => Math.min(videos.length - 1, prev + 1));
  }, [videos]);

  const lastVideo = useCallback(() => {
    setSelectedVideoIndex((prev) => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (!videos) {
      return setSelectedVideoIndex(0);
    }

    if (location.state && location.state.videoId) {
      setSelectedVideoIndex(Math.max(0, videos.findIndex((video) => video.id === location.state.videoId) ?? 0));
    } else {
      setSelectedVideoIndex(0);
    }
  }, [videos, location.state]);

  const selectedVideo = videos[selectedVideoIndex];

  const audioUrl = useStylepackAudio({ selectedVideo });

  const renderContentTemplate = useCallback(
    (content: ContentTemplate | GraphicItem, index: number) => {
      return (
        <Container
          width="156px"
          height="90px"
          padding="3px"
          className="cursor-pointer"
          onClick={() => setSelectedVideoIndex(index)}
        >
          <Container
            borderRadius="9px"
            width={'148px'}
            height={'84px'}
            aspectRatio={'16/9'}
            backgroundImage={content.thumbnailUrl}
            hoverBackground="var(--gray-1000)"
            className={classNames(styles.videoCard, { [styles.selected]: selectedVideoIndex === index })}
          ></Container>
        </Container>
      );
    },
    [selectedVideoIndex]
  );

  const goBack = () => {
    if (history.state && history.state.idx > 1) {
      history.back();
      return;
    }

    navigate('../..', { relative: 'path', replace: true });
  };

  useEffect(() => {
    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, []);

  return (
    <Portal
      id="preview-stylepack"
      className={styles.main}
      style={
        {
          '--bloom-image': `url(${selectedVideo?.thumbnailUrl})`,
          overflow: 'hidden',
        } as CSSProperties
      }
    >
      <Container className="flex jc-space-between" tagName="header" padding="16px 24px 16px 24px" width="100%">
        <Container className="flex ai-center">
          <Button onClick={goBack} className="flex ai-center">
            <Icon name={ICONS.ARROW_LEFT} />
          </Button>
          <Text tag="h5" className="H5">
            {selectedStylepack?.name}
          </Text>
        </Container>

        <Container className="flex ai-center">
          <PillTabs
            tabs={Object.values(VideoType).filter((type) => availableTypes.includes(type))}
            defaultValue={type}
            onChange={(tab) => {
              navigate('.', { relative: 'path', state: {}, replace: true });
              setType(tab);
            }}
          />
        </Container>

        <Container className="flex ai-center">
          <OptionalComponent display={!previewingShots}>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              designSize="small"
              loading={saving}
              onClick={chooseStylepack}
            >
              <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
                Choose Stylepack
              </Text>
            </Button>
          </OptionalComponent>
        </Container>
      </Container>
      <Container maxWidth="1440px" margin="auto" padding="24px 96px 40px 96px">
        <Container width="100%" height="90px">
          {contentTemplates && (
            <CustomCarousel<ContentTemplate | GraphicItem>
              itemContainerDimensions={{ width: '156px', height: '90px' }}
              list={previewingShots ? greenroomVideos : contentTemplates}
              renderItem={renderContentTemplate}
              gap={'18px'}
              forceJustifyCenter={contentTemplates.length <= 5}
              scrollTo={{ index: selectedVideoIndex }}
              virtualized
            />
          )}
        </Container>
        <Container width="100%">
          {selectedVideo?.videoUrl && (
            <VideoPlayer
              nextVideo={nextVideo}
              lastVideo={lastVideo}
              canSelectNext={selectedVideoIndex < (contentTemplates?.length || 1) - 1}
              canSelectLast={selectedVideoIndex > 0}
              videoUrl={selectedVideo.videoUrl}
              audioUrl={audioUrl}
              thumbnailUrl={selectedVideo.thumbnailUrl}
              isLower={selectedVideo?.contentType === videoTypeMap.Lowers}
            />
          )}
        </Container>
      </Container>
    </Portal>
  );
}
